import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { rebuildPlannedLesson } from '@platform/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { jobQuery } from '../library/sources';

import { placeOf, REBUILD_LABEL, rebuildPhase } from './actions';
import { ResearchOption } from './ResearchOption';

import type { RebuildPhase } from './actions';
import type { PlannedLesson } from '@platform/schema/curriculum';

export interface RebuildTarget {
  lessonId: string;
  builtLessonId: string;
  name: string;
  place: string;
}

/** What a row or the drawer hands over: Curriculum decides which dialog it opens. */
export type RebuildRow = Pick<
  PlannedLesson,
  'lessonId' | 'name' | 'course' | 'module' | 'builtLessonId' | 'published'
>;

export const rebuildTargetOf = (
  row: RebuildRow,
  builtLessonId: string,
): RebuildTarget => ({
  lessonId: row.lessonId,
  builtLessonId,
  name: row.name,
  place: placeOf(row),
});

const NOW: Record<RebuildPhase, string> = {
  rebuilding: 'Writing the new scenes, questions, feedback and visuals.',
  qa: 'Reviewing the rebuilt lesson before it replaces the old one.',
  done: 'The rebuilt lesson replaced the old one and is still a draft.',
  failed: 'The rebuild did not finish. The lesson is as it was.',
};

/**
 * Confirms a rebuild, then follows its job to the end — CURR-35. A published lesson
 * reaches this only after Move to draft: the API refuses to rebuild it.
 */
export function RebuildDialog({
  target,
  onClose,
  onStarted,
}: {
  target: RebuildTarget | null;
  onClose: () => void;
  onStarted: (jobId: string) => void;
}) {
  const queries = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [research, setResearch] = useState(false);

  const start = useTrackedMutation('Rebuild a lesson', {
    mutationFn: async (lessonId: string) =>
      (
        await rebuildPlannedLesson({
          path: { lessonId },
          body: { research },
          throwOnError: true,
        })
      ).data,
    onSuccess: async (build) => {
      setJobId(build.jobId);
      onStarted(build.jobId);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });

  // The page's progress bar polls this job; the dialog reads the same cache.
  const job = useQuery({ ...jobQuery(jobId ?? ''), enabled: jobId !== null });
  const phase = job.data === undefined ? undefined : rebuildPhase(job.data);
  const settled = phase === 'done' || phase === 'failed';

  const error = job.data?.rows.find((row) => row.error !== undefined)?.error;

  return (
    <Dialog open={target !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {jobId === null ? 'Rebuild this lesson?' : 'Rebuilding the lesson'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {target !== null && (
            <Typography>
              {target.lessonId} · {target.name}
              <br />
              {target.place}
            </Typography>
          )}
          {jobId === null ? (
            <>
              <Typography color="text.secondary">
                This will replace its generated scenes, questions, feedback, and
                visuals, and any changes made by hand. The previous version
                stays in its history. The rebuilt lesson will remain a draft.
              </Typography>
              <ResearchOption
                checked={research}
                disabled={start.isPending}
                onChange={setResearch}
              />
            </>
          ) : (
            <Stack spacing={1} role="status" aria-live="polite">
              <Typography variant="subtitle1" component="p">
                {phase === undefined ? 'Starting' : REBUILD_LABEL[phase]}
              </Typography>
              {!settled && (
                <LinearProgress
                  aria-label={`Rebuilding ${target?.lessonId ?? ''}`}
                />
              )}
              {phase !== undefined && (
                <Typography color="text.secondary">{NOW[phase]}</Typography>
              )}
              {phase === 'failed' && error !== undefined && (
                <Alert severity="error">{error}</Alert>
              )}
            </Stack>
          )}
          {start.isError && (
            <Alert severity="error">{describeApiError(start.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {jobId !== null && (
          <Link
            component={RouterLink}
            to={`/authoring/jobs/${jobId}`}
            variant="body2"
            sx={{ mr: 'auto', ml: 1 }}
          >
            Open job
          </Link>
        )}
        <Button onClick={onClose}>{jobId === null ? 'Cancel' : 'Close'}</Button>
        {jobId === null ? (
          <Button
            variant="contained"
            disabled={target === null || start.isPending || start.isSuccess}
            onClick={() => {
              if (target !== null) start.mutate(target.lessonId);
            }}
          >
            Rebuild lesson
          </Button>
        ) : (
          phase === 'done' &&
          target !== null && (
            <Button
              variant="contained"
              component={RouterLink}
              to={`/authoring/lessons/${target.builtLessonId}`}
            >
              Open rebuilt lesson
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
}
